import { type FC, useCallback, useMemo } from "react";
import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { Tag } from "@/shared/components/ui/tag";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { COST_TYPE_CONFIG } from "./AddOngoingCost";

interface ChecklistItem {
  value: string;
  label: string;
  description?: string;
}

function buildChecklistItems(): ChecklistItem[] {
  return [
    {
      value: "ongoing_property_costs",
      label: "Ongoing property costs",
      description: "Mortgage, property tax, HOA, utilities, insurance",
    },
    {
      value: "storage_units",
      label: "Storage units",
      description: "Monthly storage rental fees",
    },
    {
      value: "subscriptions",
      label: "Subscriptions",
      description: "Streaming, memberships, recurring charges",
    },
  ];
}

const NONE_VALUE = "none_of_the_above";
const ALL_COST_KEYS = buildChecklistItems().map((i) => i.value);

export const OngoingCostsChecklist: FC<FlowCustomComponentProps> = ({
  onComplete,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { debts } = useEstateItems(estateId);
  const items = buildChecklistItems();

  const rawSelected = answers["ongoing_costs_selected"];
  const rawArray: string[] = Array.isArray(rawSelected) ? rawSelected : [];

  const addedKeys = useMemo(() => {
    const existingItemTypes = new Set(debts.map((item) => item.item_type));
    const keys = new Set<string>();
    for (const [key, config] of Object.entries(COST_TYPE_CONFIG)) {
      if (existingItemTypes.has(config.itemType)) keys.add(key);
    }
    return keys;
  }, [debts]);

  const selected = useMemo(() => {
    const merged = new Set([...rawArray, ...addedKeys]);
    if (rawArray.includes(NONE_VALUE)) merged.add(NONE_VALUE);
    return [...merged];
  }, [rawArray, addedKeys]);

  const isNoneSelected = selected.includes(NONE_VALUE);

  const toggle = useCallback(
    (value: string) => {
      if (value === NONE_VALUE) {
        if (isNoneSelected) {
          setAnswer("ongoing_costs_selected", [...addedKeys]);
        } else {
          setAnswer("ongoing_costs_selected", [...addedKeys, NONE_VALUE]);
        }
        return;
      }
      if (addedKeys.has(value)) return;
      const next = new Set(selected);
      next.delete(NONE_VALUE);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      setAnswer("ongoing_costs_selected", [...next]);
    },
    [selected, isNoneSelected, addedKeys, setAnswer],
  );

  const hasSelection = selected.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        {items.map((item) => {
          const isAdded = addedKeys.has(item.value);
          const isDisabled = isNoneSelected && !isAdded;
          return (
            <div key={item.value} className="relative">
              <OptionCard
                label={item.label}
                description={item.description}
                selected={isAdded || (!isNoneSelected && selected.includes(item.value))}
                onClick={() => !isDisabled && !isAdded && toggle(item.value)}
                variant="checkbox"
                className={
                  isDisabled
                    ? "opacity-50 pointer-events-none"
                    : isAdded
                      ? "pointer-events-none"
                      : ""
                }
              />
              {isAdded && (
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                  <Tag color="green" icon={Check} iconStroke={2.5}>
                    Added
                  </Tag>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 py-1">
        <div className="h-px flex-1 bg-border-primary" />
        <span className="text-sm text-secondary">OR</span>
        <div className="h-px flex-1 bg-border-primary" />
      </div>

      <OptionCard
        label="None of the above"
        selected={isNoneSelected}
        onClick={() => toggle(NONE_VALUE)}
        variant="checkbox"
      />

      <div className="flex flex-col items-center gap-3">
        <Button
          variant="brand"
          rounded
          className="w-full max-w-[400px]"
          onClick={() => {
            setAnswer("ongoing_costs_selected", selected);
            onComplete();
          }}
          disabled={!hasSelection}
        >
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
        <Button
          variant="link"
          className="text-secondary"
          onClick={() => {
            setAnswer("ongoing_costs_selected", [...addedKeys, NONE_VALUE]);
            onComplete();
          }}
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
};

export { ALL_COST_KEYS, NONE_VALUE };
